/**
 * EmailHandoverModal — extracted from DesignPackTab.js (v260.76).
 * Pure mechanical move; no behaviour change.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Mail, X, Eye, FileText, Download, ChevronRight, AlertCircle,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { DesignPackPreviewBody } from '../DesignPackPreviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function EmailHandoverModal({ estimateId, onClose, onSent }) {
  const [defaults, setDefaults] = useState(null);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [includeXlsx, setIncludeXlsx] = useState(true);
  const [loadingDefaults, setLoadingDefaults] = useState(true);
  const [sending, setSending] = useState(false);
  // v260.65 — Inline "Preview attachments" pane. Hidden by default
  // so the modal stays compact for users who just want to fire and
  // forget; opens to a tab strip (Word + Excel) when the user wants
  // to eyeball what's about to leave the building.
  const [showPreview, setShowPreview] = useState(false);
  const [previewKind, setPreviewKind] = useState('docx');

  // Fetch suggested recipients + subject/body on mount. The server
  // looks at the estimate's owner + the approval audit trail (CC) so
  // sales doesn't have to retype the stakeholder list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingDefaults(true);
      try {
        const r = await axios.get(
          `${API_URL}/api/estimates/${estimateId}/design-pack/email/defaults`,
          { withCredentials: true },
        );
        if (cancelled) return;
        const d = r.data;
        setDefaults(d);
        setTo((d.to || []).join(', '));
        setCc((d.cc || []).join(', '));
        setSubject(d.subject || '');
        // Strip HTML for the textarea — sales mostly tweaks copy, the
        // backend re-wraps in HTML if they leave it plain.
        setMessage((d.message || '').replace(/<[^>]+>/g, '').trim());
      } catch (e) {
        if (!cancelled) {
          toast.error('Could not load email defaults');
        }
      } finally {
        if (!cancelled) setLoadingDefaults(false);
      }
    })();
    return () => { cancelled = true; };
  }, [estimateId]);

  const parseEmails = (raw) => (raw || '')
    .split(/[,;\n]+/)
    .map(s => s.trim())
    .filter(s => s && s.includes('@'));

  const handleSend = async () => {
    const toList = parseEmails(to);
    const ccList = parseEmails(cc);
    if (!toList.length) {
      toast.error('Add at least one recipient');
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/estimates/${estimateId}/design-pack/email`,
        {
          to: toList,
          cc: ccList,
          subject: subject.trim() || undefined,
          // If the user kept the default HTML message we don't want to
          // post the plain-text version (loses bullet structure). Send
          // the trimmed plain message — server falls back to default
          // HTML when blank.
          message: message.trim() || undefined,
          include_xlsx: includeXlsx,
        },
        { withCredentials: true },
      );
      const n = r.data?.attachment_count || 0;
      toast.success(`Handover Pack emailed to ${toList.length} recipient${toList.length === 1 ? '' : 's'} (${n} attachment${n === 1 ? '' : 's'})`);
      onSent && onSent(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="design-pack-email-modal">
      <div className={`bg-white rounded-xl shadow-2xl ${showPreview ? 'max-w-5xl' : 'max-w-lg'} w-full max-h-[90vh] overflow-hidden flex flex-col transition-[max-width] duration-200`}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#4ABFCF]" />
            <h2 className="text-sm font-semibold text-[#0F2040]">Email Handover Pack</h2>
            {defaults?.is_draft && (
              <Badge className="bg-amber-100 text-amber-700 text-[9px] py-0">DRAFT pack</Badge>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="design-pack-email-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className={`flex-1 overflow-hidden flex ${showPreview ? 'flex-row' : 'flex-col'} min-h-0`}>
          {/* Compose form column */}
          <div className={`overflow-y-auto p-4 space-y-3 ${showPreview ? 'w-[420px] flex-shrink-0 border-r border-slate-200' : 'flex-1'}`}>
            {loadingDefaults ? (
              <div className="py-8 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
              {defaults?.is_draft && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-800 flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  This pack hasn&apos;t been finalized — the email will be marked as a draft preview. Consider finalizing first if this is the official kickoff hand-off.
                </div>
              )}

              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-1 block">To <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  value={to}
                  onChange={e => setTo(e.target.value)}
                  placeholder="implementation-lead@…, sales@…"
                  className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:border-[#4ABFCF] focus:outline-none"
                  data-testid="design-pack-email-to"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-1 flex items-center justify-between">
                  <span>CC <span className="text-slate-300 normal-case">(pre-filled from approval stakeholders)</span></span>
                </label>
                <input
                  type="text"
                  value={cc}
                  onChange={e => setCc(e.target.value)}
                  placeholder="stakeholder@…"
                  className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:border-[#4ABFCF] focus:outline-none"
                  data-testid="design-pack-email-cc"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-1 block">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:border-[#4ABFCF] focus:outline-none"
                  data-testid="design-pack-email-subject"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-1 block">Message</label>
                <Textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={6}
                  className="text-xs"
                  data-testid="design-pack-email-message"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  Plain text. The server wraps it in the standard Seertech HTML chrome.
                </p>
              </div>

              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeXlsx}
                  onChange={e => setIncludeXlsx(e.target.checked)}
                  className="accent-[#0F2040]"
                  data-testid="design-pack-email-include-xlsx"
                />
                Include Excel checklist attachment
              </label>

              <div className="rounded-md border border-slate-200 bg-slate-50/60 p-2.5 text-[11px] text-slate-600">
                <strong className="text-[#0F2040]">Attachments:</strong>{' '}
                Word document{includeXlsx && ' + Excel checklist'}.
                {' '}The Word doc is the narrative deliverable; the Excel file is the live implementation checklist consultants will mark up.
              </div>

              {/* v260.65 — One-click "Preview attachments" toggle that
                  drops the same DOCX/XLSX renderer used by the tab's
                  Eye buttons inline next to the compose form, so the
                  sender can eyeball exactly what the recipient will
                  open before Send fires. */}
              <button
                type="button"
                onClick={() => setShowPreview(p => !p)}
                className="w-full text-left text-xs font-medium text-[#0F2040] hover:bg-[#4ABFCF]/10 border border-[#4ABFCF]/30 rounded-md px-2.5 py-2 flex items-center justify-between transition"
                data-testid="design-pack-email-preview-toggle"
              >
                <span className="flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5 text-[#4ABFCF]" />
                  {showPreview ? 'Hide attachment preview' : 'Preview attachments before sending'}
                </span>
                {showPreview ? <ChevronRight className="w-3.5 h-3.5 rotate-180" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            </>
          )}
        </div>

        {/* Inline preview pane (visible only when toggled). Hosts a
            small tab strip switching between Word and Excel previews
            so the sender can flip between the narrative deliverable
            and the live checklist without leaving the modal. */}
        {showPreview && !loadingDefaults && (
          <div className="flex-1 flex flex-col min-h-0 bg-slate-50/40" data-testid="design-pack-email-preview-pane">
            <div className="flex items-center gap-1 px-3 pt-3 border-b border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => setPreviewKind('docx')}
                className={`text-xs px-3 py-1.5 rounded-t border-b-2 transition flex items-center gap-1 ${
                  previewKind === 'docx'
                    ? 'border-[#4ABFCF] text-[#0F2040] font-semibold bg-[#4ABFCF]/5'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
                data-testid="design-pack-email-preview-tab-docx"
              >
                <FileText className="w-3.5 h-3.5" /> Word
              </button>
              {includeXlsx && (
                <button
                  type="button"
                  onClick={() => setPreviewKind('xlsx')}
                  className={`text-xs px-3 py-1.5 rounded-t border-b-2 transition flex items-center gap-1 ${
                    previewKind === 'xlsx'
                      ? 'border-[#4ABFCF] text-[#0F2040] font-semibold bg-[#4ABFCF]/5'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="design-pack-email-preview-tab-xlsx"
                >
                  <Download className="w-3.5 h-3.5" /> Excel
                </button>
              )}
              <div className="ml-auto text-[10px] text-slate-400 pr-2">
                What the recipient will open
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <DesignPackPreviewBody
                key={previewKind /* force remount on tab switch so the wrong-format render clears */}
                kind={previewKind === 'xlsx' && !includeXlsx ? 'docx' : previewKind}
                estimateId={estimateId}
                height="100%"
                testIdSuffix="email"
              />
            </div>
          </div>
        )}
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50/40">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={sending} data-testid="design-pack-email-cancel">Cancel</Button>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={sending || loadingDefaults}
            className="bg-[#4ABFCF] hover:bg-[#3FA8B6]"
            data-testid="design-pack-email-send"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Regenerate Modal ─────────────────────────────────────────────────


export default EmailHandoverModal;
